import { z } from 'zod';

import type { ActivityDefinition } from './types.js';

import { trimmed } from './_checks.js';
import { draftFields } from './_shared.js';
import { activityIssue, asList, asRecord } from './types.js';

export const TapRevealTerm = z.looseObject({
  term: z.string().optional(),
  definition: z.string().optional(),
});

const TapRevealSetup = z.looseObject({
  ...draftFields,
  statement: z.string().optional(),
  terms: z.array(TapRevealTerm).optional(),
});

/**
 * Tap-define and cloze-reveal are one activity read two ways: a statement, some terms
 * inside it, and a tap that opens each one. Define leaves the words showing; cloze
 * blanks them until tapped. Nothing else about them differs, so they share a builder —
 * but each still ships as its own module, because a caller dispatches on the type it has.
 *
 * The check both need is the same, and it is the one that actually bites: the runtime
 * locates each term *inside* the statement. A term that is not in there renders nothing
 * at all — no blank, no tap target — so the player is asked to open something invisible.
 */
const tapRevealDefinition = (
  type: string,
  summary: string,
): ActivityDefinition => ({
  type,
  summary,
  setup: TapRevealSetup,
  check: (setup, ctx) => {
    const issue = activityIssue(type);
    const statement = trimmed(setup.statement);
    const terms = asList(setup.terms);
    const issues = [];

    if (!statement) {
      issues.push(issue('noStatement', 'No statement is written — the terms have nothing to sit in.', [...ctx.path, 'setup', 'statement'], ctx));
    }

    if (!terms.length) {
      issues.push(issue('empty', 'No terms are marked — there is nothing for the player to tap.', [...ctx.path, 'setup', 'terms'], ctx));
    }

    for (const [index, raw] of terms.entries()) {
      const entry = asRecord(raw);
      const term = trimmed(entry.term);
      const path = [...ctx.path, 'setup', 'terms', index];

      if (!term) {
        issues.push(issue('termText', `Term ${index + 1} has no word.`, [...path, 'term'], ctx, raw));
        continue;
      }
      if (!trimmed(entry.definition)) {
        issues.push(issue('termDefinition', `Term '${term}' has no definition behind it.`, [...path, 'definition'], ctx, raw));
      }
      if (statement && !statement.toLowerCase().includes(term.toLowerCase())) {
        issues.push(issue(
          'termNotInStatement',
          `The statement never says '${term}', so the runtime has nothing to turn into a tap target.`,
          [...path, 'term'],
          ctx,
          term,
        ));
      }
    }

    return issues;
  },
});

export const tapDefine = tapRevealDefinition(
  'tap_define',
  'A statement whose key words the player taps to see a definition.',
);

export const clozeReveal = tapRevealDefinition(
  'cloze_reveal',
  'A statement with words blanked out until the player taps to reveal each one.',
);
